/**
 * Template content validation engine (PRO-0130 §6 / D7).
 *
 * PURE, deterministic, side-effect free (no DB, no logging) — exactly like
 * `audience/dedupeRecipients` and `restrictions/applyRestrictions` — so the
 * matrix is unit-tested exhaustively and UC-EP3-002 (compose preview) /
 * UC-EP3-003 (merge fields) reuse it without re-implementation. It is the SINGLE
 * source of truth for the merge-token vocabulary and SMS length, and a PUBLISH
 * GATE (the service refuses to publish content that does not validate).
 *
 * SF-SEC-002: every `ValidationIssue.detail` is structured metadata only — token
 * name, character position, segment counts — NEVER the template body or a body
 * fragment.
 */
import type {
  SmsEncoding,
  TemplateChannels,
  ValidationIssue,
} from '../types/template';
import { calcSmsSegments } from '../sms/segmentation';

/** The merge-token vocabulary. The single source of truth (shared downstream). */
export const ALLOWED_TOKENS = [
  'carer_name',
  'child_name',
  'school_name',
  'sender_name',
] as const;

export type AllowedToken = (typeof ALLOWED_TOKENS)[number];

/**
 * Maximum concatenated SMS segments a single template may compile to. The body
 * cap is 2000 chars (handler-level); this is the additional operational guard so
 * an accidental wall-of-text SMS is blocked at publish rather than fanned out at
 * dispatch. Tunable single source; confirm at the SLO/comms workshop.
 */
export const MAX_SMS_SEGMENTS = 10;

const ALLOWED = new Set<string>(ALLOWED_TOKENS);
const IDENTIFIER = /^[a-z_][a-z0-9_]*$/i;

export interface ContentValidationResult {
  readonly ok: boolean;
  readonly issues: readonly ValidationIssue[];
  readonly tokens: readonly string[]; // distinct allow-list tokens found (sorted)
  readonly smsSegments: number | null; // null when not sms-applicable
  readonly smsEncoding: SmsEncoding | null;
}

interface TokenScan {
  readonly tokens: Set<string>;
  readonly issues: ValidationIssue[];
}

/**
 * Scan a single text field for `{{token}}` occurrences. Valid syntax is exactly
 * `{{ identifier }}` (optional surrounding whitespace). Anything else is a
 * malformed/unknown finding. Single-brace `{identifier}` groups are treated as
 * a mistyped token (malformed). Positions are 0-based indexes into `text`.
 */
function scanTokens(text: string): TokenScan {
  const tokens = new Set<string>();
  const issues: ValidationIssue[] = [];
  let i = 0;

  while (i < text.length) {
    const open = text.indexOf('{{', i);
    if (open === -1) {
      break;
    }
    const close = text.indexOf('}}', open + 2);
    if (close === -1) {
      issues.push({ code: 'malformed_token', detail: `pos=${open} kind=unterminated` });
      break;
    }
    const inner = text.slice(open + 2, close);
    if (inner.includes('{') || inner.includes('}')) {
      // Nested or stray braces inside the token, e.g. `{{ {{x}} }}`.
      issues.push({ code: 'malformed_token', detail: `pos=${open} kind=nested` });
      i = close + 2;
      continue;
    }
    const name = inner.trim();
    if (name === '') {
      issues.push({ code: 'malformed_token', detail: `pos=${open} kind=empty` });
    } else if (!IDENTIFIER.test(name)) {
      issues.push({ code: 'malformed_token', detail: `pos=${open} kind=invalid_identifier` });
    } else if (!ALLOWED.has(name)) {
      issues.push({ code: 'unknown_token', detail: name });
    } else {
      tokens.add(name);
    }
    i = close + 2;
  }

  // Single-brace token attempts, e.g. `{carer_name}` — flagged malformed. Only a
  // bare-identifier group counts (a likely mistyped {{token}}); standalone braces
  // are legal GSM-7 characters and are left alone.
  const singleBrace = /(?<!\{)\{\s*([a-z_][a-z0-9_]*)\s*\}(?!\})/gi;
  let m: RegExpExecArray | null;
  while ((m = singleBrace.exec(text)) !== null) {
    issues.push({ code: 'malformed_token', detail: `pos=${m.index} kind=single_brace` });
  }

  return { tokens, issues };
}

/**
 * Validate template content: merge-token allow-list (body + subject) and, when
 * the channel includes SMS, GSM-7/UCS-2 segment calculation against
 * `MAX_SMS_SEGMENTS`. Returns the distinct allow-list tokens found and the
 * computed SMS metadata (stored on the version row for fast downstream reads).
 */
export function validateTemplateContent(input: {
  readonly body: string;
  readonly subject: string | null;
  readonly channels: TemplateChannels;
}): ContentValidationResult {
  const issues: ValidationIssue[] = [];
  const tokens = new Set<string>();

  const trimmedBody = input.body.trim();
  if (trimmedBody === '') {
    issues.push({ code: 'empty_body', detail: '' });
  }

  const bodyScan = scanTokens(input.body);
  bodyScan.tokens.forEach((t) => tokens.add(t));
  issues.push(...bodyScan.issues);

  if (input.subject !== null) {
    const subjectScan = scanTokens(input.subject);
    subjectScan.tokens.forEach((t) => tokens.add(t));
    issues.push(...subjectScan.issues);
  }

  let smsSegments: number | null = null;
  let smsEncoding: SmsEncoding | null = null;
  const smsApplicable = input.channels === 'sms' || input.channels === 'both';
  if (smsApplicable) {
    const calc = calcSmsSegments(input.body);
    smsSegments = calc.segments;
    smsEncoding = calc.encoding;
    if (calc.segments > MAX_SMS_SEGMENTS) {
      issues.push({
        code: 'sms_over_limit',
        detail: `segments=${calc.segments} limit=${MAX_SMS_SEGMENTS} encoding=${calc.encoding}`,
      });
    }
  }

  return {
    ok: issues.length === 0,
    issues,
    tokens: [...tokens].sort(),
    smsSegments,
    smsEncoding,
  };
}
